import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, setToken } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

export default function AuthCallback({ onAuthComplete }) {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserData } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) {
      console.log('[AuthCallback] Already processed, skipping');
      return;
    }
    hasProcessed.current = true;

    console.log('[AuthCallback] === Processing auth callback ===');
    console.log('[AuthCallback] Full URL:', window.location.href);
    console.log('[AuthCallback] Hash:', window.location.hash);
    console.log('[AuthCallback] Search:', window.location.search);
    console.log('[AuthCallback] location.hash:', location.hash);
    console.log('[AuthCallback] location.search:', location.search);

    // Try to extract session_id from hash first, then search params
    let sessionId = null;
    
    // Check hash
    const hash = window.location.hash;
    if (hash) {
      const hashParams = new URLSearchParams(hash.replace('#', ''));
      sessionId = hashParams.get('session_id');
      console.log('[AuthCallback] Session ID from hash:', sessionId ? sessionId.substring(0, 15) + '...' : 'null');
    }
    
    // Check search params if not found in hash
    if (!sessionId) {
      const search = window.location.search;
      if (search) {
        const searchParams = new URLSearchParams(search);
        sessionId = searchParams.get('session_id');
        console.log('[AuthCallback] Session ID from search:', sessionId ? sessionId.substring(0, 15) + '...' : 'null');
      }
    }

    if (!sessionId) {
      console.log('[AuthCallback] No session_id found anywhere, redirecting to login');
      if (onAuthComplete) {
        onAuthComplete();
      }
      navigate('/login', { replace: true });
      return;
    }

    console.log('[AuthCallback] Found session_id, processing...');

    const processSession = async () => {
      try {
        console.log('[AuthCallback] Calling processSession API...');
        const response = await auth.processSession(sessionId);
        console.log('[AuthCallback] API response status:', response.status);
        console.log('[AuthCallback] API response data keys:', Object.keys(response.data));
        
        // Store the session token in localStorage for Bearer auth
        if (response.data.session_token) {
          console.log('[AuthCallback] Storing session token in localStorage');
          setToken(response.data.session_token);
        } else {
          console.warn('[AuthCallback] WARNING: No session_token in response!');
        }
        
        if (response.data.user) {
          console.log('[AuthCallback] Setting user data for:', response.data.user.email);
          setUserData(response.data.user);
        } else {
          console.warn('[AuthCallback] WARNING: No user in response!');
        }
        
        // Clear hash/search and navigate
        console.log('[AuthCallback] Success! Navigating to dashboard');
        window.history.replaceState(null, '', '/dashboard');
        
        // Signal parent that auth is complete BEFORE navigating
        if (onAuthComplete) {
          onAuthComplete();
        }
        
        navigate('/dashboard', { replace: true, state: { user: response.data.user } });
      } catch (error) {
        console.error('[AuthCallback] ERROR processing session:', error);
        console.error('[AuthCallback] Error response:', error.response?.data);
        console.error('[AuthCallback] Error status:', error.response?.status);
        
        // Signal parent that auth is complete even on error
        if (onAuthComplete) {
          onAuthComplete();
        }
        
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [navigate, setUserData, location, onAuthComplete]);

  // Show a loading state while processing
  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-stone-950">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-4 border-stone-300 border-t-stone-600 rounded-full animate-spin"></div>
        <p className="text-stone-500">Signing you in...</p>
      </div>
    </div>
  );
}
